import { Component, inject, Input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
	BadgeComponent,
	CopyTextComponent,
	DividerComponent,
	IconComponent,
	InfoGroupComponent,
	LinkComponent,
	TagComponent,
	TagIconComponent,
} from '@tecalisdev/infinity';
import { formatDate, formatDuration, truncateUUID, NOT_AVAILABLE_DISPLAY } from '../../../../../core/utils/utils';
import { RolePermissionsService } from '../../../../../core/services/role-permissions.service';
import { OPERATION_STATUS } from '../../../../../operations/models/operations.models';

export type OperationDetailOperationMetaVariant = 'process' | 'operation';
export type OperationDetailOperationMetaTriesStyle = 'badge' | 'tag';

/**
 * Meta OPERATION compartida (UUID, proceso, tries, fechas, duración + campos opcionales por tipo).
 * Solo primitives `@tecalisdev/infinity`; el padre prepara labels opcionales.
 */
@Component({
	selector: 'app-operation-detail-operation-meta',
	imports: [
		TranslatePipe,
		InfoGroupComponent,
		DividerComponent,
		CopyTextComponent,
		BadgeComponent,
		TagComponent,
		TagIconComponent,
		IconComponent,
		LinkComponent,
	],
	templateUrl: './operation-detail-operation-meta.component.html',
	styleUrl: './operation-detail-operation-meta.component.scss',
})
export class OperationDetailOperationMetaComponent {
	@Input() variant: OperationDetailOperationMetaVariant = 'operation';
	@Input() operationUuid?: string | null;
	@Input() processUuid?: string | null;
	@Input() startAt?: string | null;
	@Input() endAt?: string | null;
	@Input() totalTime?: number | null;

	/** Si se define, muestra Nº intentos con ese estilo. */
	@Input() triesStyle: OperationDetailOperationMetaTriesStyle | null = null;
	@Input() triesLabel?: string | null;
	@Input() state?: OPERATION_STATUS | string | null;
	@Input() showKoOnTries = false;

	@Input() captureMethodLabel?: string | null;
	@Input() showMiDni = false;
	@Input() typeLabel?: string | null;
	@Input() obtainMethodLabel?: string | null;
	@Input() verificationLabel?: string | null;

	public rolePermissions = inject(RolePermissionsService);
	public OPERATION_STATUS = OPERATION_STATUS;

	private readonly uuidTruncateHead = 8;
	private readonly uuidTruncateTail = 8;

	public displayTruncatedUuid(uuid?: string | null): string {
		if (!uuid) return NOT_AVAILABLE_DISPLAY;
		return truncateUUID(uuid, this.uuidTruncateHead, this.uuidTruncateTail);
	}

	public formatStartDate(): string {
		return formatDate(this.startAt ?? undefined);
	}

	public formatEndDate(): string {
		return formatDate(this.endAt ?? undefined);
	}

	public formatOperationDuration(): string {
		if (this.totalTime === null || this.totalTime === undefined) {
			return NOT_AVAILABLE_DISPLAY;
		}
		return formatDuration(this.totalTime);
	}

	public resolvedTriesLabel(): string {
		if (this.triesLabel != null && this.triesLabel !== '') {
			return this.triesLabel;
		}
		return NOT_AVAILABLE_DISPLAY;
	}

	public get showProcessLink(): boolean {
		return this.variant === 'operation' && !!this.processUuid;
	}

	public get showKoIcon(): boolean {
		return this.showKoOnTries && this.state === OPERATION_STATUS.KO;
	}
}
